import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest } from '@/lib/api/client';
import { useStudyText } from '@/i18n/studyText';
import { workflowTitles, type WorkflowModule } from '../workflowApi';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { routes } from '@/lib/routes';
import { AdvisorExchangePage } from '@/features/advisor/components/AdvisorExchangePage';
import { DoctoralSocialPanel } from './DoctoralSocialPanel';
import { DoctoralCoordinationPanel } from './DoctoralCoordinationPanel';

type Notice = {
  id: string;
  title: string;
  body: string;
  href: string;
  read_at: string | null;
  kind: string;
};
export function DoctoralContactHub({
  onClose,
  advisor = false,
}: {
  onClose: () => void;
  advisor?: boolean;
}) {
  const { locale } = useI18n();
  const { refreshUser } = useAuth();
  const st = useStudyText();
  const text = useCallback(
    (ru: string, kz: string, en: string) => (locale === 'kz' ? kz : locale === 'en' ? en : ru),
    [locale],
  );
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'messages';
  const [responsibilities, setResponsibilities] = useState<
    { kind: string; group: string; start: string; end: string }[]
  >([]);
  const [leader, setLeader] = useState(false);
  const [email, setEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [challenge, setChallenge] = useState('');
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [cursor, setCursor] = useState<string | null>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const [base, page] = await Promise.all([
          apiRequest<{
            leader: unknown;
            email: string;
            email_verified: boolean;
            responsibilities?: typeof responsibilities;
          }>('/doctoral/coordination', { channel: 'platform' }),
          apiRequest<{ items: Notice[]; next_cursor: string | null }>(
            '/doctoral/advisor-exchange/notices',
            { channel: 'platform' },
          ),
        ]);
        if (!cancelled) {
          setLeader(Boolean(base.leader));
          setResponsibilities(base.responsibilities || []);
          setError('');
          setEmail(base.email);
          setVerified(base.email_verified);
          setNotices(page.items);
          setCursor(page.next_cursor);
        }
      } catch {
        if (!cancelled)
          setError(
            text(
              'Не удалось загрузить связь с эдвайзером',
              'Эдвайзермен байланысты жүктеу мүмкін болмады',
              'Unable to load advisor contact',
            ),
          );
      }
    };
    void refresh();
    const timer = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [locale, text, tab]);
  const tabs = [
    ['messages', text('Сообщения', 'Хабарламалар', 'Messages')],
    [
      'announcements',
      text('Объявления и задания', 'Хабарландырулар және тапсырмалар', 'Announcements and tasks'),
    ],
    ['duties', text('Дежурства / мероприятия', 'Кезекшілік / іс-шаралар', 'Duties / events')],
    ['notifications', text('Уведомления', 'Хабарлар', 'Notifications')],
    ['social', text('Заявки для соцсетей', 'Әлеуметтік желіге өтінімдер', 'Social media requests')],
    ['email', text('Моя почта', 'Менің поштам', 'My email')],
  ];
  return (
    <div>
      <h1 className="px-4 pt-4">
        {text('Связь с эдвайзером', 'Эдвайзермен байланыс', 'Contact your advisor')}
      </h1>
      <nav
        aria-label={text('Связь с эдвайзером', 'Эдвайзермен байланыс', 'Contact your advisor')}
        className="flex flex-wrap gap-2 p-4"
      >
        {tabs.map(([id, title]) => (
          <button
            key={id}
            aria-pressed={tab === id}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('tab', id);
              setParams(next);
            }}
          >
            {title}
            {id === 'notifications' && notices.filter((n) => !n.read_at).length
              ? ` (${notices.filter((n) => !n.read_at).length})`
              : ''}
          </button>
        ))}
        {!advisor && (
          <>
            {leader && (
              <button onClick={() => navigate(`${routes.doctoral}/group`)}>
                {text('Моя группа', 'Менің тобым', 'My group')}
              </button>
            )}
            <button onClick={() => navigate(`${routes.doctoral}/surveys`)}>
              {text('Опросы', 'Сауалнамалар', 'Surveys')}
            </button>
            <button onClick={() => navigate('/app/profile/account')}>
              {text('Мой профиль', 'Менің профилім', 'My profile')}
            </button>
          </>
        )}
      </nav>
      {responsibilities.map((r) => (
        <p key={`${r.kind}-${r.group}`} className="px-4">
          {r.kind === 'SOCIAL_PUBLICATIONS'
            ? text(
                'Ответственный за публикации',
                'Жарияланымдарға жауапты',
                'Publication coordinator',
              )
            : text(
                'Ответственный за стажировку',
                'Тағылымдамаға жауапты',
                'Internship coordinator',
              )}{' '}
          · {r.group} · {r.start} — {r.end}
        </p>
      ))}
      {error && <p role="alert">{error}</p>}
      {tab === 'messages' && <AdvisorExchangePage embedded advisor={advisor} onClose={onClose} />}
      {(tab === 'announcements' || tab === 'duties') && (
        <DoctoralCoordinationPanel advisor={advisor} kind={tab} onClose={onClose} />
      )}
      {tab === 'notifications' && (
        <section className="p-6">
          <button onClick={() => navigate(routes.notifications)}>
            {text('Настройки уведомлений', 'Хабар баптаулары', 'Notification settings')}
          </button>
          <button onClick={onClose}>{text('Назад', 'Артқа', 'Back')}</button>
          {!notices.length && (
            <p>{text('Уведомлений пока нет', 'Әзірге хабарлар жоқ', 'No notifications yet')}</p>
          )}
          {notices.map((n) => (
            <article key={n.id} className="p-3">
              <strong>
                {n.kind === 'MESSAGE'
                  ? text('Связь с эдвайзером', 'Эдвайзермен байланыс', 'Contact your advisor')
                  : n.kind === 'REMINDER'
                    ? text(
                        'Напоминание старосты',
                        'Старостаның еске салуы',
                        'Group leader reminder',
                      )
                    : n.title}
              </strong>
              <p>
                {n.kind === 'REMINDER'
                  ? st(workflowTitles[n.body as WorkflowModule] || n.body)
                  : n.body === 'Назначение отменено'
                    ? text('Назначение отменено', 'Тағайындау тоқтатылды', 'Assignment cancelled')
                    : n.body}
              </p>
              <button
                onClick={async () => {
                  try {
                    await apiRequest(`/doctoral/advisor-exchange/notices/${n.id}/read`, {
                      channel: 'platform',
                      method: 'POST',
                    });
                    setNotices((old) =>
                      old.map((v) =>
                        v.id === n.id ? { ...v, read_at: new Date().toISOString() } : v,
                      ),
                    );
                    navigate(n.href);
                  } catch {
                    setError(
                      text(
                        'Не удалось открыть уведомление',
                        'Хабарды ашу мүмкін болмады',
                        'Unable to open notification',
                      ),
                    );
                  }
                }}
              >
                {text('Открыть', 'Ашу', 'Open')}
              </button>
            </article>
          ))}
          {cursor && (
            <button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const page = await apiRequest<{ items: Notice[]; next_cursor: string | null }>(
                    '/doctoral/advisor-exchange/notices',
                    { channel: 'platform', query: { before: cursor } },
                  );
                  setNotices((old) => [...old, ...page.items]);
                  setCursor(page.next_cursor);
                } catch {
                  setError(text('Ошибка загрузки', 'Жүктеу қатесі', 'Loading error'));
                } finally {
                  setBusy(false);
                }
              }}
            >
              {text('Ещё', 'Тағы', 'More')}
            </button>
          )}
        </section>
      )}
      {tab === 'social' && <DoctoralSocialPanel onClose={onClose} />}
      {tab === 'email' && (
        <section className="p-6">
          <button onClick={onClose}>{text('Назад', 'Артқа', 'Back')}</button>
          <p>
            {email} ·{' '}
            {verified
              ? text('Подтверждён', 'Расталған', 'Verified')
              : text('Не подтверждён', 'Расталмаған', 'Unverified')}
          </p>
          <p>
            {text(
              'Подтверждённый адрес используется для входа и уведомлений Академии.',
              'Расталған мекенжай Академияға кіру және хабарландырулар үшін пайдаланылады.',
              'The verified address is used for Academy login and notifications.',
            )}
          </p>
          <label>
            {text('Email', 'Email', 'Email')}
            <input
              type="email"
              value={newEmail}
              disabled={busy}
              maxLength={255}
              onChange={(e) => {
                setNewEmail(e.target.value);
                setChallenge('');
              }}
            />
          </label>
          <button
            disabled={busy || !newEmail.trim()}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                const result = await apiRequest<{ challenge_id: string }>(
                  '/doctoral/advisor-exchange/email/start',
                  { channel: 'platform', method: 'POST', body: { email: newEmail } },
                );
                setChallenge(result.challenge_id);
              } catch (e) {
                setError(
                  e instanceof Error
                    ? e.message
                    : text('Ошибка отправки', 'Жіберу қатесі', 'Delivery error'),
                );
              } finally {
                setBusy(false);
              }
            }}
          >
            {text('Отправить код', 'Код жіберу', 'Send code')}
          </button>
          {challenge && (
            <>
              <label>
                {text('Код из письма', 'Хаттағы код', 'Email code')}
                <input
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  disabled={busy}
                  onChange={(e) => setCode(e.target.value)}
                />
              </label>
              <button
                disabled={busy || !/^\d{6}$/.test(code)}
                onClick={async () => {
                  setBusy(true);
                  setError('');
                  try {
                    const result = await apiRequest<{ email: string }>(
                      '/doctoral/advisor-exchange/email/confirm',
                      {
                        channel: 'platform',
                        method: 'POST',
                        body: { challenge_id: challenge, code },
                      },
                    );
                    setEmail(result.email);
                    setVerified(true);
                    void refreshUser().catch(() => undefined);
                    setChallenge('');
                    setCode('');
                  } catch (e) {
                    setError(
                      e instanceof Error
                        ? e.message
                        : text('Ошибка подтверждения', 'Растау қатесі', 'Verification error'),
                    );
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {text('Подтвердить email', 'Email растау', 'Verify email')}
              </button>
            </>
          )}
        </section>
      )}
    </div>
  );
}
